import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { MetricCard } from '@/components/ui/metric-card'
import { Timer, Brain, AudioLines, Mic, Split } from 'lucide-react'
import type { VoicePipelineSummary } from '@/lib/api/reporting.client'
import { cn } from '@/lib/utils'

/** Convert seconds to display milliseconds, rounded to integer. */
function msDisplay(seconds: number): string {
  return `${Math.round(seconds * 1000)} ms`
}

function latencyColor(ms: number, thresholds: [number, number]): string {
  if (ms < thresholds[0]) return 'text-success-dim'
  if (ms < thresholds[1]) return 'text-warning-dim'
  return 'text-error-dim'
}

/** Color the "spoke before classify" rate — higher means the switch lost the race more often. */
function rateColor(rate: number): string {
  if (rate < 0.05) return 'text-success-dim'
  if (rate < 0.2) return 'text-warning-dim'
  return 'text-error-dim'
}

function PercentileMetric({
  p50,
  p90,
  p95,
  thresholds,
}: {
  p50: number
  p90: number
  p95: number
  thresholds: [number, number]
}) {
  const p50Ms = p50 * 1000
  return (
    <div className="grid grid-cols-3 gap-1 text-center w-full">
      <div>
        <div className="text-[10px] text-muted-foreground mb-0.5">P50</div>
        <div
          className={cn(
            'text-base font-bold leading-tight',
            latencyColor(p50Ms, thresholds),
          )}
        >
          {msDisplay(p50)}
        </div>
      </div>
      <div>
        <div className="text-[10px] text-muted-foreground mb-0.5">P90</div>
        <div className="text-base font-bold leading-tight text-foreground">
          {msDisplay(p90)}
        </div>
      </div>
      <div>
        <div className="text-[10px] text-muted-foreground mb-0.5">P95</div>
        <div className="text-base font-bold leading-tight text-foreground">
          {msDisplay(p95)}
        </div>
      </div>
    </div>
  )
}

interface VoicePipelineKPICardsProps {
  summary: VoicePipelineSummary | null
  isLoading: boolean
}

function LoadingCards() {
  return (
    <>
      {[1, 2, 3, 4, 5].map(i => (
        <Card key={i} className="border border-border">
          <CardContent className="p-3 relative">
            <Skeleton className="absolute -top-2 -left-2 h-8 w-8 rounded-lg" />
            <Skeleton className="h-4 w-24 mx-auto mb-3 mt-2" />
            <Skeleton className="h-10 w-16 mx-auto mb-3" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
      ))}
    </>
  )
}

export function VoicePipelineKPICards({
  summary,
  isLoading,
}: VoicePipelineKPICardsProps) {
  if (isLoading || !summary) {
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <LoadingCards />
      </div>
    )
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
      <MetricCard
        icon={Timer}
        title="Total Latency"
        metric={
          <PercentileMetric
            p50={summary.totalLatencyP50}
            p90={summary.totalLatencyP90}
            p95={summary.totalLatencyP95}
            thresholds={[1500, 2500]}
          />
        }
        description="End-to-end response time"
        info="Time from user finishing speaking to hearing the first syllable of the agent's response"
      />
      <MetricCard
        icon={Brain}
        title="LLM TTFT"
        metric={
          <PercentileMetric
            p50={summary.llmTtftP50}
            p90={summary.llmTtftP90}
            p95={summary.llmTtftP95}
            thresholds={[500, 1000]}
          />
        }
        description="Time to first LLM token"
        info="How quickly the LLM starts generating a response"
      />
      <MetricCard
        icon={AudioLines}
        title="TTS TTFB"
        metric={
          <PercentileMetric
            p50={summary.ttsTtfbP50}
            p90={summary.ttsTtfbP90}
            p95={summary.ttsTtfbP95}
            thresholds={[300, 600]}
          />
        }
        description="Time to first audio byte"
        info="How quickly the TTS model starts producing audio"
      />
      <MetricCard
        icon={Mic}
        title="EOU Delay"
        metric={
          <PercentileMetric
            p50={summary.eouDelayP50}
            p90={summary.eouDelayP90}
            p95={summary.eouDelayP95}
            thresholds={[300, 600]}
          />
        }
        description="End-of-utterance delay"
        info="Time between user finishing speaking and the system deciding to act. Total latency = EOU + LLM TTFT + TTS TTFB"
      />
      <MetricCard
        icon={Split}
        title="Scenario Classification"
        metric={
          <div className="w-full space-y-1.5">
            <PercentileMetric
              p50={summary.scenarioClassificationP50}
              p90={summary.scenarioClassificationP90}
              p95={summary.scenarioClassificationP95}
              thresholds={[500, 1000]}
            />
            <div
              className={cn(
                'text-[10px] text-center font-medium',
                rateColor(summary.responseBeforeClassificationRate),
              )}
            >
              {Math.round(summary.responseBeforeClassificationRate * 100)}%
              spoke before classify
            </div>
          </div>
        }
        description="Intent classification latency"
        info="Background per-turn scenario classification. Must resolve faster than the agent's LLM + TTS response so a scenario switch can override it. 'Spoke before classify' = share of classified turns where the agent's first audio went out before classification resolved (the switch was too late)"
      />
    </div>
  )
}
